import { z } from 'zod';

import { IntakeRuleCreatedVia } from '../generated/enums.js';

/**
 * AP intake rules (ALC-3653/ALC-3654): tenant-level deterministic routing rules evaluated once per
 * structured email intake message at triage. See docs/features/ap-intake-rules/SPEC.md.
 */

/**
 * Structured intake sources an `ApIntakeRule.source` can scope to (matched case-sensitively — see
 * `evaluateIntakeRules`'s `source` condition field and the two triage hooks). Extend this list
 * when a new producer lands; a rule's `source: null` still means "any source".
 */
export const INTAKE_SOURCES = ['pipefy', 'email'] as const;
export type IntakeSource = (typeof INTAKE_SOURCES)[number];
export const IntakeSourceSchema = z.enum(INTAKE_SOURCES);

/** The generic (non-Pipefy) email triage path's `source` value — see `ChannelTriageService`. */
export const GENERIC_EMAIL_INTAKE_SOURCE: IntakeSource = INTAKE_SOURCES[1];

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

export type IntakeRuleConditionField =
  | { kind: 'rawField'; key: string }
  | { kind: 'subject' }
  | { kind: 'sender' }
  | { kind: 'body' }
  | { kind: 'source' };

export const IntakeRuleConditionFieldSchema: z.ZodType<IntakeRuleConditionField> =
  z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('rawField'), key: z.string().trim().min(1).max(100) }),
    z.object({ kind: z.literal('subject') }),
    z.object({ kind: z.literal('sender') }),
    z.object({ kind: z.literal('body') }),
    z.object({ kind: z.literal('source') }),
  ]);

export const IntakeRuleOperator = {
  equals: 'equals',
  not_equals: 'not_equals',
  in: 'in',
  not_in: 'not_in',
  contains: 'contains',
  /**
   * Regex match. Matched case-insensitively against the *normalized* field text (NFD, diacritics
   * stripped, whitespace collapsed — see `normalizeIntakeText`), not the raw value — the pattern
   * itself is not normalized, so an accented character in the pattern (e.g. `café`) will never
   * match, since the text it is tested against never contains one. Write patterns without
   * diacritics.
   */
  matches: 'matches',
  is_empty: 'is_empty',
  is_not_empty: 'is_not_empty',
} as const;
export type IntakeRuleOperator = (typeof IntakeRuleOperator)[keyof typeof IntakeRuleOperator];

export const INTAKE_RULE_OPERATOR_VALUES = [
  IntakeRuleOperator.equals,
  IntakeRuleOperator.not_equals,
  IntakeRuleOperator.in,
  IntakeRuleOperator.not_in,
  IntakeRuleOperator.contains,
  IntakeRuleOperator.matches,
  IntakeRuleOperator.is_empty,
  IntakeRuleOperator.is_not_empty,
] as const satisfies readonly IntakeRuleOperator[];

export const IntakeRuleOperatorSchema = z.enum(INTAKE_RULE_OPERATOR_VALUES);

/** `matches` operators without a value never match anything and no operator requires >1 value item. */
export const OPERATORS_REQUIRING_NO_VALUE: readonly IntakeRuleOperator[] = [
  IntakeRuleOperator.is_empty,
  IntakeRuleOperator.is_not_empty,
];
export const OPERATORS_REQUIRING_ARRAY_VALUE: readonly IntakeRuleOperator[] = [
  IntakeRuleOperator.in,
  IntakeRuleOperator.not_in,
];

/** Fail-closed cap for the `matches` operator pattern (SPEC.md §3). */
export const INTAKE_RULE_PATTERN_MAX_LENGTH = 100;

export interface IntakeRuleCondition {
  field: IntakeRuleConditionField;
  operator: IntakeRuleOperator;
  value?: string | string[];
}

export const IntakeRuleConditionSchema: z.ZodType<IntakeRuleCondition> = z.object({
  field: IntakeRuleConditionFieldSchema,
  operator: IntakeRuleOperatorSchema,
  value: z.union([z.string().max(200), z.array(z.string().max(200)).min(1).max(50)]).optional(),
});

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type IntakeRuleValue = { literal: string | number | boolean } | { field: string };

export const IntakeRuleValueSchema: z.ZodType<IntakeRuleValue> = z.union([
  z.object({ literal: z.union([z.string().max(500), z.number(), z.boolean()]) }),
  z.object({ field: z.string().trim().min(1).max(100) }),
]);

const FIXED_INTAKE_RULE_TARGETS = [
  'bill.entityId',
  'bill.description',
  'bill.memo',
  'bill.billCompetencyDate',
  'bill.paymentMethod',
  'bill.currencyCode',
  'bill.tags',
  'bill.isUrgent',
  'classification.departmentId',
  'classification.costCenterId',
  'classification.glAccountId',
  'classification.managerialAccountId',
  'payment.originBankAccountId',
  'payment.paymentMethod',
  'payment.scheduledDate',
  // ALC-4476 (D1): the literal COST_PROVISION marks EVERY line of the bill as paying a provision.
  'line.destination',
] as const;

export type FixedIntakeRuleTarget = (typeof FIXED_INTAKE_RULE_TARGETS)[number];
export const FIXED_INTAKE_RULE_TARGET_VALUES: readonly FixedIntakeRuleTarget[] =
  FIXED_INTAKE_RULE_TARGETS;

/** Amounts and due date are never targets — documents keep financial truth (SPEC.md §3). */
export type IntakeRuleTarget = FixedIntakeRuleTarget | `classification.segment.${string}`;

const SEGMENT_TARGET_PATTERN = /^classification\.segment\.([^.]{1,100})$/;

/**
 * The targets applied against `ApPayment` rather than `ApBill`. They are written by a separate
 * step that can legitimately do nothing (no eligible principal payment, or the update throws), so
 * callers that record an audit trail must treat them apart from the bill-scoped targets.
 */
const PAYMENT_INTAKE_RULE_TARGETS: readonly string[] = [
  'payment.originBankAccountId',
  'payment.paymentMethod',
  'payment.scheduledDate',
];

export function isPaymentIntakeRuleTarget(target: string): boolean {
  return PAYMENT_INTAKE_RULE_TARGETS.includes(target);
}

export function isSegmentIntakeRuleTarget(
  target: string,
): target is `classification.segment.${string}` {
  return SEGMENT_TARGET_PATTERN.test(target);
}

export function parseSegmentIdFromIntakeRuleTarget(target: string): string | null {
  return SEGMENT_TARGET_PATTERN.exec(target)?.[1] ?? null;
}

export function isIntakeRuleTarget(value: string): value is IntakeRuleTarget {
  return (
    (FIXED_INTAKE_RULE_TARGETS as readonly string[]).includes(value) ||
    isSegmentIntakeRuleTarget(value)
  );
}

export const IntakeRuleTargetSchema = z.string().min(1).max(140).refine(isIntakeRuleTarget, {
  message: 'Unknown intake rule target',
}) as z.ZodType<IntakeRuleTarget>;

export interface IntakeRuleAction {
  target: IntakeRuleTarget;
  value: IntakeRuleValue;
}

export const IntakeRuleActionSchema: z.ZodType<IntakeRuleAction> = z.object({
  target: IntakeRuleTargetSchema,
  value: IntakeRuleValueSchema,
});

// ---------------------------------------------------------------------------
// Rule input (create/update payload) and the persisted shape
// ---------------------------------------------------------------------------

export const IntakeRuleInputSchema = z.object({
  inboxId: z.string().uuid().nullable().optional(),
  source: IntakeSourceSchema.nullable().optional(),
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).nullable().optional(),
  priority: z.number().int().min(0).max(100_000).optional(),
  isActive: z.boolean().optional(),
  conditions: z.array(IntakeRuleConditionSchema).min(1).max(20),
  actions: z.array(IntakeRuleActionSchema).min(1).max(20),
});
export type IntakeRuleInput = z.infer<typeof IntakeRuleInputSchema>;

export const IntakeRuleUpdateSchema = IntakeRuleInputSchema.partial();
export type IntakeRuleUpdateInput = z.infer<typeof IntakeRuleUpdateSchema>;

export interface IntakeRule {
  tenantId: string;
  intakeRuleId: string;
  inboxId: string | null;
  source: string | null;
  name: string;
  description: string | null;
  priority: number;
  isActive: boolean;
  conditions: IntakeRuleCondition[];
  actions: IntakeRuleAction[];
  createdVia: IntakeRuleCreatedVia;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string | null;
}

// ---------------------------------------------------------------------------
// Evaluator input/output
// ---------------------------------------------------------------------------

export interface IntakeRuleEvaluationInput {
  tenantId: string;
  inboxId?: string | null;
  source?: string | null;
  rawFields: Record<string, string>;
  subject?: string | null;
  sender?: string | null;
  body?: string | null;
}

/**
 * The subset of `IntakeRule` the pure evaluator needs, ordered by the caller. `inboxId`/`source`
 * are carried through unused by `evaluateIntakeRules` itself (scoping is applied by the caller's DB
 * query on the live triage path) but are needed by `replayIntakeRules`, which evaluates arbitrary
 * rule/row combinations and must apply each rule's own scope per row (SPEC.md §7).
 */
export type IntakeRuleEvaluatorRule = Pick<
  IntakeRule,
  'intakeRuleId' | 'name' | 'priority' | 'conditions' | 'actions' | 'inboxId' | 'source'
>;

export interface IntakeDecisionMatchedRule {
  intakeRuleId: string;
  name: string;
  priority: number;
}

export interface IntakeDecisionUnresolved {
  intakeRuleId: string;
  target: IntakeRuleTarget;
  reason: string;
}

export interface IntakeDecisionTargetValue {
  value: unknown;
  intakeRuleId: string;
}

export interface IntakeDecision {
  version: 1;
  evaluatedAt: string;
  matchedRules: IntakeDecisionMatchedRule[];
  targets: Partial<Record<IntakeRuleTarget, IntakeDecisionTargetValue>>;
  unresolved: IntakeDecisionUnresolved[];
}

/** Zero rules matched (or zero rules configured) → behaviour identical to today (SPEC.md §2). */
export function isEmptyIntakeDecision(decision: Pick<IntakeDecision, 'matchedRules'>): boolean {
  return decision.matchedRules.length === 0;
}

// ---------------------------------------------------------------------------
// Replay (SPEC.md §7)
// ---------------------------------------------------------------------------

export interface IntakeRuleReplayPerRuleResult {
  intakeRuleId: string;
  matched: number;
}

export interface IntakeRuleReplayResult {
  total: number;
  matched: number;
  perRule: IntakeRuleReplayPerRuleResult[];
}

// ---------------------------------------------------------------------------
// Prompt compiler (SPEC.md §7): tenant catalog snapshot given to the LLM and the rule
// proposals it returns. Shared by packages/ai (compiler input/output), apps/api (builds the
// snapshot, runs save-time validation + replay) and apps/mcp (propose/replay tool output).
// ---------------------------------------------------------------------------

export interface IntakeRuleCatalogEntity {
  entityId: string;
  name: string;
}

export interface IntakeRuleCatalogDepartment {
  departmentId: string;
  entityId: string;
  name: string;
}

export interface IntakeRuleCatalogCostCenter {
  costCenterId: string;
  entityId: string;
  name: string;
}

export interface IntakeRuleCatalogGlAccount {
  glAccountId: string;
  code: string;
  name: string;
}

export interface IntakeRuleCatalogManagerialAccount {
  managerialAccountId: string;
  entityId: string;
  code: string;
  name: string;
}

export interface IntakeRuleCatalogSegment {
  segmentId: string;
  entityId: string;
  name: string;
  hasPredefinedValues: boolean;
  dataType: string;
}

export interface IntakeRuleCatalogBankAccount {
  bankAccountId: string;
  entityId: string;
  name: string;
}

/**
 * Tenant catalog snapshot handed to the prompt compiler so it can name catalog objects by id
 * instead of inventing them. `rawFieldSamples` are the known `rawFields` keys of the inbox, each
 * with up to 5 sample values taken from the last 200 `PipefyCardIntakeReservation` rows (SPEC.md §7).
 */
export interface IntakeRuleCatalogSnapshot {
  entities: IntakeRuleCatalogEntity[];
  departments: IntakeRuleCatalogDepartment[];
  costCenters: IntakeRuleCatalogCostCenter[];
  glAccounts: IntakeRuleCatalogGlAccount[];
  managerialAccounts: IntakeRuleCatalogManagerialAccount[];
  segments: IntakeRuleCatalogSegment[];
  bankAccounts: IntakeRuleCatalogBankAccount[];
  rawFieldSamples: Record<string, string[]>;
}

/**
 * One compiler-proposed rule draft, before save-time validation. `rationale` is the model's
 * one-line justification. The prompt-injection guard (SPEC.md §7) caps every action literal
 * string value at {@link INTAKE_RULE_PROPOSAL_LITERAL_MAX_LENGTH} chars — stricter than
 * {@link IntakeRuleValueSchema}'s general 500-char cap, which S2/S5 also rely on and this schema
 * must not narrow for them.
 */
export const INTAKE_RULE_PROPOSAL_LITERAL_MAX_LENGTH = 200;

function actionLiteralStringsWithinInjectionCap(actions: IntakeRuleAction[]): boolean {
  return actions.every((action) => {
    if (!('literal' in action.value) || typeof action.value.literal !== 'string') return true;
    return action.value.literal.length <= INTAKE_RULE_PROPOSAL_LITERAL_MAX_LENGTH;
  });
}

export const IntakeRuleProposalDraftSchema = z.object({
  rule: IntakeRuleInputSchema,
  rationale: z.string().trim().min(1).max(500),
});
export type IntakeRuleProposalDraft = z.infer<typeof IntakeRuleProposalDraftSchema>;

/**
 * Full compiler structured-output contract: the LLM must return a bounded list of proposals,
 * each a well-formed {@link IntakeRuleInput} (so the target whitelist and operator list already
 * reject an injection attempt naming an unknown target) with every action literal string within
 * the tighter compiler-specific cap.
 */
export const IntakeRuleCompilerOutputSchema = z.object({
  proposals: z
    .array(IntakeRuleProposalDraftSchema)
    .max(10)
    .refine(
      (proposals) => proposals.every((p) => actionLiteralStringsWithinInjectionCap(p.rule.actions)),
      {
        message: `Action literal string values must be at most ${INTAKE_RULE_PROPOSAL_LITERAL_MAX_LENGTH} characters`,
      },
    ),
});
export type IntakeRuleCompilerOutputBatch = z.infer<typeof IntakeRuleCompilerOutputSchema>;

export interface IntakeRuleProposalValidationError {
  code: IntakeRuleErrorCode;
  target?: string;
  message: string;
  messagePtBr?: string;
}

export interface IntakeRuleProposalValidation {
  ok: boolean;
  errors: IntakeRuleProposalValidationError[];
}

/**
 * A compiler draft after being run through the S1 save validator (never persisted). `rule` is a
 * full {@link IntakeRuleInput} for a well-formed proposal; a proposal the compiler could not map
 * onto a well-formed rule at all (SPEC.md §7 — "never silently dropped") carries only whatever
 * fragment survived (usually just `name`) plus `validation.ok = false`.
 */
export interface IntakeRuleProposal {
  rule: Partial<IntakeRuleInput> & Pick<IntakeRuleInput, 'name'>;
  rationale: string;
  validation: IntakeRuleProposalValidation;
}

/**
 * One compiler-proposed rule the agent mapped onto a well-formed, whitelisted {@link IntakeRuleInput}
 * (still unvalidated against the tenant catalog — the caller runs the S1 save validator).
 */
export interface IntakeRuleCompilerDraftProposal {
  status: 'draft';
  draft: IntakeRuleProposalDraft;
}

/**
 * One compiler-proposed rule that could not be mapped onto a well-formed, whitelisted
 * {@link IntakeRuleInput} at all (a structural LLM slip, e.g. an empty rawFields key, or an
 * aggregate-level guard rejecting the whole batch — see `packages/ai`'s compiler). Reported to the
 * caller with a stable code, never silently dropped (SPEC.md §7).
 */
export interface IntakeRuleCompilerRejectedProposal {
  status: 'rejected';
  name: string;
  rationale: string;
  code: IntakeRuleErrorCode;
  message: string;
  messagePtBr: string;
}

export type IntakeRuleCompilerProposalResult =
  IntakeRuleCompilerDraftProposal | IntakeRuleCompilerRejectedProposal;

/**
 * The prompt compiler's structured contract with its caller (`packages/services/ap/intake-rules
 * /propose.ts`): one entry per proposal the model returned — a mappable draft or a stable-coded
 * rejection — so the array length always equals what the model returned (SPEC.md §7).
 */
export interface IntakeRuleCompilerOutput {
  proposals: IntakeRuleCompilerProposalResult[];
}

// ---------------------------------------------------------------------------
// Stable error codes (pt-BR + en) — save-time validation (API + MCP, SPEC.md §3)
// ---------------------------------------------------------------------------

export const IntakeRuleErrorCode = {
  INTAKE_RULE_NOT_FOUND: 'INTAKE_RULE_NOT_FOUND',
  INTAKE_RULE_TARGET_NOT_FOUND: 'INTAKE_RULE_TARGET_NOT_FOUND',
  INTAKE_RULE_TARGET_ENTITY_MISMATCH: 'INTAKE_RULE_TARGET_ENTITY_MISMATCH',
  INTAKE_RULE_SEGMENT_NOT_TEXT: 'INTAKE_RULE_SEGMENT_NOT_TEXT',
  INTAKE_RULE_TARGET_FORBIDDEN: 'INTAKE_RULE_TARGET_FORBIDDEN',
  INTAKE_RULE_TARGET_INVALID_VALUE: 'INTAKE_RULE_TARGET_INVALID_VALUE',
  INTAKE_RULE_PATTERN_INVALID: 'INTAKE_RULE_PATTERN_INVALID',
  INTAKE_RULE_FIELD_REQUIRED: 'INTAKE_RULE_FIELD_REQUIRED',
  INTAKE_RULE_ENTITY_SCOPE_REQUIRED: 'INTAKE_RULE_ENTITY_SCOPE_REQUIRED',
  INTAKE_RULE_INBOX_NOT_FOUND: 'INTAKE_RULE_INBOX_NOT_FOUND',
  /** The compiler could not map a model-returned proposal onto a well-formed, whitelisted rule. */
  INTAKE_RULE_PROPOSAL_MALFORMED: 'INTAKE_RULE_PROPOSAL_MALFORMED',
  /** The prompt compiler call itself failed (timeout, provider error, refusal) — not a validation issue. */
  INTAKE_RULE_COMPILER_FAILED: 'INTAKE_RULE_COMPILER_FAILED',
} as const;
export type IntakeRuleErrorCode = (typeof IntakeRuleErrorCode)[keyof typeof IntakeRuleErrorCode];

export interface IntakeRuleErrorMessage {
  ptBr: string;
  en: string;
}

export const INTAKE_RULE_ERROR_MESSAGES: Record<IntakeRuleErrorCode, IntakeRuleErrorMessage> = {
  INTAKE_RULE_NOT_FOUND: {
    ptBr: 'Regra de entrada não encontrada.',
    en: 'Intake rule not found.',
  },
  INTAKE_RULE_TARGET_NOT_FOUND: {
    ptBr: 'O valor informado não corresponde a um registro ativo do catálogo do tenant.',
    en: 'The value provided does not match an active catalog record for this tenant.',
  },
  INTAKE_RULE_TARGET_ENTITY_MISMATCH: {
    ptBr: 'O registro selecionado pertence a uma entidade diferente da definida pela regra (bill.entityId).',
    en: 'The selected record belongs to a different entity than the one set by the rule (bill.entityId).',
  },
  INTAKE_RULE_SEGMENT_NOT_TEXT: {
    ptBr: 'Um valor de texto livre só é permitido para segmentos sem valores predefinidos do tipo texto.',
    en: 'A free-text value is only allowed for non-predefined, text-typed segments.',
  },
  INTAKE_RULE_TARGET_FORBIDDEN: {
    ptBr: 'Este destino não pode ser definido por uma regra de entrada.',
    en: 'This target cannot be set by an intake rule.',
  },
  INTAKE_RULE_TARGET_INVALID_VALUE: {
    ptBr: 'O valor informado não é válido para este destino de regra de entrada.',
    en: 'The value provided is invalid for this intake-rule target.',
  },
  INTAKE_RULE_PATTERN_INVALID: {
    ptBr: 'O padrão da condição "matches" é inválido ou excede 100 caracteres.',
    en: 'The "matches" condition pattern is invalid or exceeds 100 characters.',
  },
  INTAKE_RULE_FIELD_REQUIRED: {
    ptBr: 'Campo obrigatório ausente na regra de entrada.',
    en: 'Required field missing on the intake rule.',
  },
  INTAKE_RULE_ENTITY_SCOPE_REQUIRED: {
    ptBr: 'Este destino exige uma entidade definida pela regra ou pela caixa de entrada.',
    en: 'This target requires an entity set by the rule or its inbox.',
  },
  INTAKE_RULE_INBOX_NOT_FOUND: {
    ptBr: 'A caixa de entrada informada não existe neste tenant.',
    en: 'The given inbox does not belong to this tenant.',
  },
  INTAKE_RULE_PROPOSAL_MALFORMED: {
    ptBr: 'Não foi possível interpretar esta proposta de regra gerada por IA.',
    en: 'This AI-generated rule proposal could not be understood.',
  },
  INTAKE_RULE_COMPILER_FAILED: {
    ptBr: 'Não foi possível compilar o pedido em regras no momento. Tente novamente.',
    en: 'Could not compile the request into rules right now. Please try again.',
  },
};
